import { ClientType } from 'storeTypes'
import { routes } from './constants'

export type OpenClientFromSearchDeps = {
    canUseFarmerId: boolean
    pathname: string
    navigate: (to: string, opts?: { replace?: boolean }) => void
    onShowFarmerIdModal: (client: ClientType) => void
    getClientOrders: (clientCode: string) => void
}

export const buildClientProfilePath = (client: ClientType): string => {
    const clientDetailBaseUrl = `/village/${client.village_id}/client/${client.client_code}`
    return client.all_enrollment <= 0
        ? `${clientDetailBaseUrl}/previous`
        : clientDetailBaseUrl
}

export const seedClientForProfile = (client: ClientType): ClientType => {
    const stored = {
        ...client,
        client_code: String(client.client_code)
    }
    localStorage.setItem('client_details', JSON.stringify(stored))
    return stored
}

export const openClientFromSearch = (
    client: ClientType,
    deps: OpenClientFromSearchDeps
): void => {
    const stored = seedClientForProfile(client)
    if (deps.canUseFarmerId && !stored.duplicate) {
        deps.getClientOrders(stored.client_code)
        deps.onShowFarmerIdModal(stored)
        return
    }
    deps.navigate(`${buildClientProfilePath(stored)}?from=${deps.pathname}`, {
        replace: true
    })
}

export type VillageLike = { sf_id: string }

export type ResolveEnrollmentPathResult =
    | { kind: 'navigate'; path: string }
    | { kind: 'select-village'; enrollBaseUrl: string }
    | { kind: 'error'; messageKey: string }

export const resolveEnrollmentPath = async ({
    veVillages,
    pathname,
    getBlankIdCount,
    villageId
}: {
    veVillages: VillageLike[]
    pathname: string
    getBlankIdCount: () => Promise<{ blank_id_count: number }>
    villageId?: string
}): Promise<ResolveEnrollmentPathResult> => {
    const totalBlankIdResponse = await getBlankIdCount()

    if (totalBlankIdResponse?.blank_id_count < 1) {
        return { kind: 'error', messageKey: 'enrollment.BlankIDsDepleted' }
    }

    const enrollBaseUrl = `/${routes.enroll}/`

    // ATC open-village entry: village and return path are both fixed.
    // Do not use pathname here — that is the phone screen (/client-search).
    if (villageId) {
        const encodedVillageId = encodeURIComponent(villageId)
        return {
            kind: 'navigate',
            path: `${enrollBaseUrl}${encodedVillageId}?from=/village/${encodedVillageId}`
        }
    }

    if (veVillages.length === 1) {
        return {
            kind: 'navigate',
            path: `${enrollBaseUrl}${veVillages[0]?.sf_id}?from=${pathname}`
        }
    }

    return { kind: 'select-village', enrollBaseUrl }
}
